import { type ReactNode } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Plus, Trash2, Pencil } from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

type AdminBlogCategory = {
  id: string
  name: string
  slug: string
  description: string | null
  position: number
  posts_count: number
}

type Props = {
  categories: AdminBlogCategory[]
}

export default function AdminBlogCategoriesIndex({ categories }: Props) {
  function destroy(category: AdminBlogCategory) {
    if (!confirm(`Delete "${category.name}"? Posts in this category will become uncategorized.`)) return
    router.delete(`/admin/blog_categories/${category.id}`)
  }

  return (
    <>
      <Head title="Admin · Blog categories" />
      <div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight">Blog categories</h1>
            <Link
              href="/admin/blog_posts"
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              ← Back to posts
            </Link>
          </div>
          <Button asChild>
            <Link href="/admin/blog_categories/new">
              <Plus className="size-4" />
              New category
            </Link>
          </Button>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Posts</TableHead>
                <TableHead>Order</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                    No categories yet.
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{c.slug}</TableCell>
                    <TableCell className="text-sm">{c.posts_count}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.position}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button asChild variant="ghost" size="sm">
                          <Link href={`/admin/blog_categories/${c.id}/edit`}>
                            <Pencil className="size-4" />
                          </Link>
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => destroy(c)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  )
}

AdminBlogCategoriesIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
